/**
 * Patient Profile Page
 * Updated to integrate with onboarding system and handle different completion states
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Avatar,
  Divider,
  Stack,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Badge,
  Tooltip,
  InputLabel,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  PhotoCamera as PhotoCameraIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Language as LanguageIcon,
  AccessibilityNew as AccessibilityIcon,
  CheckCircle,
  RadioButtonUnchecked,
  Person,
  ContactPhone,
  History,
  CloudUpload,
  Assignment,
  ArrowForward,
  Dashboard,
  Warning,
  Info
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

// API Services
import { 
  patientOnboardingService, 
  validateProfile, 
  formatProfileData,
  parseProfileData 
} from '../../services/api/patientOnboarding';
import { 
  biometricsApi, 
  documentsApi, 
  intakeFormApi 
} from '../../services/api/patientComprehensive';

// Auth Context
import { useAuth } from '../../hooks/useAuth';

const Profile = () => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Onboarding state
  const [onboardingState, setOnboardingState] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [profileExists, setProfileExists] = useState(false);

  // Form data using comprehensive structure
  const [formData, setFormData] = useState({
    fullName: '',
    dateOfBirth: null,
    gender: '',
    phoneE164: '',
    email: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    countryCode: 'IN',
    emergencyContactName: '',
    emergencyContactPhone: '',
    // Comprehensive fields
    bloodGroup: '',
    maritalStatus: '',
    education: '',
    employmentStatus: '',
    occupation: '',
    employer: '',
    insuranceProvider: '',
    insurancePolicyNumber: '',
    insuranceGroupNumber: ''
  });

  const [settings, setSettings] = useState({
    emailNotifications: true,
    smsNotifications: true,
    darkMode: false,
    accessibility: false,
    language: 'English',
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Load profile and onboarding state on mount
  useEffect(() => {
    loadProfileAndOnboardingState();
  }, []);

  // Pre-fill with user data if available
  useEffect(() => {
    if (user && !profileExists) {
      setFormData(prev => ({
        ...prev,
        fullName: prev.fullName || user.name || '',
        phoneE164: prev.phoneE164 || user.phone_number || '',
        email: prev.email || user.email || ''
      }));
    }
  }, [user, profileExists]);

  const loadProfileAndOnboardingState = async () => {
    try {
      setLoading(true);
      
      // Load profile data using the correct API
      try {
        const profileResponse = await patientOnboardingService.getProfile();
        if (profileResponse) {
          const profileData = profileResponse;
          setProfileData(profileData);
          
          // Parse the API data to form data using the existing function
          const parsedData = parseProfileData(profileData);
          setFormData(parsedData);
          setProfileExists(true);
        }
      } catch (profileErr) {
        console.log('No profile data available yet');
        setProfileExists(false);
      }

      // Load biometrics data
      try {
        const biometricsResponse = await biometricsApi.getBiometrics();
        if (biometricsResponse && biometricsResponse.length > 0) {
          const biometricsData = biometricsResponse[0];
          setProfileData(prev => ({
            ...prev,
            biometrics: biometricsData
          }));
        }
      } catch (biometricsErr) {
        console.log('No biometrics data available');
      }

      // Load documents data
      try {
        const documentsResponse = await documentsApi.getDocuments();
        if (documentsResponse && documentsResponse.length > 0) {
          setProfileData(prev => ({
            ...prev,
            documents: documentsResponse
          }));
        }
      } catch (documentsErr) {
        console.log('No documents data available');
      }

      // Load intake form data
      try {
        const intakeResponse = await intakeFormApi.getIntakeForm();
        if (intakeResponse && intakeResponse.data) {
          setProfileData(prev => ({
            ...prev,
            intakeForm: intakeResponse.data
          }));
        }
      } catch (intakeErr) {
        console.log('No intake form data available');
      }

      // Load onboarding state
      const onboarding = await patientOnboardingService.getOnboardingState();
      setOnboardingState(onboarding);
    } catch (err) {
      console.error('Error loading profile and onboarding state:', err);
      setError('Failed to load profile information');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts editing
    if (error) {
      setError(null);
    }
  };

  const handleSettingChange = (setting) => {
    setSettings((prev) => ({
      ...prev,
      [setting]: !prev[setting],
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate form data
      const validationErrors = validateProfile(formData);
      
      if (Object.keys(validationErrors).length > 0) {
        // Show first validation error
        const firstError = Object.values(validationErrors)[0];
        setError(firstError);
        setLoading(false);
        return;
      }

      // Format data for API using the existing function
      const formattedData = formatProfileData(formData);
      
      // Save profile using the correct API
      const result = await patientOnboardingService.saveProfile(formattedData);
      
      // Mark profile section as complete
      await patientOnboardingService.completeSection('profile');
      
      // Reload onboarding state
      const updatedOnboarding = await patientOnboardingService.getOnboardingState();
      setOnboardingState(updatedOnboarding);
      
      setProfileExists(true);
      setIsEditing(false);
      setSuccess('Profile updated successfully!');
      toast.success('Profile saved successfully!');
      
      // Update form data with the saved profile data
      if (result && result.profile) {
        const parsedData = parseProfileData(result.profile);
        setFormData(parsedData);
        setProfileData(result.profile);
      } else {
        // Reload profile data to show updated information
        await loadProfileAndOnboardingState();
      }
    } catch (err) {
      console.error('Error saving profile:', err);
      
      // Handle API validation errors
      if (err.response?.data?.details) {
        const apiErrors = {};
        const details = err.response.data.details;
        
        // Map API field names to form field names
        Object.keys(details).forEach(apiField => {
          const formField = mapApiFieldToFormField(apiField);
          if (formField) {
            apiErrors[formField] = Array.isArray(details[apiField]) 
              ? details[apiField][0] 
              : details[apiField];
          }
        });
        
        // Show first error
        const firstError = Object.values(apiErrors)[0];
        setError(firstError || 'Failed to update profile. Please try again.');
      } else {
        setError('Failed to update profile. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSavePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }
    
    setLoading(true);
    try {
      // For now, show a message that this feature is coming soon
      // TODO: Implement actual password change API
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulated API call
      toast.success('Password change feature coming soon!');
      setShowPasswordDialog(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (err) {
      setError('Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const mapApiFieldToFormField = (apiField) => {
    const mapping = {
      'full_name': 'fullName',
      'dob': 'dateOfBirth',
      'phone_e164': 'phoneE164',
      'email': 'email',
      'postal_code': 'postalCode',
      'blood_group': 'bloodGroup',
      'marital_status': 'maritalStatus',
      'employment_status': 'employmentStatus',
      'insurance_provider': 'insuranceProvider',
      'insurance_policy_number': 'insurancePolicyNumber',
      'insurance_group_number': 'insuranceGroupNumber'
    };
    return mapping[apiField] || apiField;
  };

  const handleSectionComplete = async (sectionKey) => {
    try {
      await patientOnboardingService.completeSection(sectionKey);
      
      // Reload onboarding state
      const updatedOnboarding = await patientOnboardingService.getOnboardingState();
      setOnboardingState(updatedOnboarding);
      
      toast.success(`${sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1)} section completed!`);
    } catch (err) {
      console.error('Error completing section:', err);
      toast.error('Failed to update section status');
    }
  };

  // Section configuration
  const sectionConfig = {
    profile: {
      icon: Person,
      title: 'Basic Profile',
      description: 'Your personal information and contact details',
      color: 'primary'
    },
    biometrics: {
      icon: AccessibilityIcon,
      title: 'Biometrics & Health',
      description: 'Height, weight, vitals, and lifestyle information',
      color: 'success'
    },
    emergency: {
      icon: ContactPhone,
      title: 'Emergency Contact',
      description: 'Add someone we can contact in case of emergency',
      color: 'secondary'
    },
    history: {
      icon: History,
      title: 'Medical History',
      description: 'Share your medical background for better care',
      color: 'info'
    },
    uploads: {
      icon: CloudUpload,
      title: 'Upload Documents',
      description: 'Upload existing medical reports and documents',
      color: 'warning'
    },
    intake: {
      icon: Assignment,
      title: 'Intake Form',
      description: 'Complete detailed health assessment',
      color: 'error'
    }
  };

  const getSectionStatus = (sectionKey) => {
    return onboardingState?.sections_done?.[sectionKey] ? 'completed' : 'pending';
  };

  // Calculate completion percentage based on section completion status
  const calculateCompletionPercentage = () => {
    if (!onboardingState?.sections_done) return 0;
    
    const sections = ['profile', 'biometrics', 'emergency', 'history', 'uploads', 'intake'];
    const completedSections = sections.filter(section => 
      onboardingState.sections_done[section]
    ).length;
    
    return Math.round((completedSections / sections.length) * 100);
  };

  const completionPercentage = calculateCompletionPercentage();

  if (loading && !profileData) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '1200px', margin: '0 auto' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <Typography variant="h4" gutterBottom>
        Profile Settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Profile Completion Status */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Profile Completion Status</Typography>
              <Chip 
                label={`${completionPercentage}% Complete`} 
                color={completionPercentage === 100 ? 'success' : 'primary'}
                variant="outlined"
              />
            </Box>
            
            <LinearProgress 
              variant="determinate" 
              value={completionPercentage} 
              sx={{ mb: 2, height: 8, borderRadius: 4 }}
            />

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your profile completion is automatically calculated based on the information you provide.
            </Typography>
          </Paper>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Stack spacing={2}>
                <Button
                  variant="outlined"
                  startIcon={<AccessibilityIcon />}
                  fullWidth
                  onClick={() => window.location.href = '/onboarding/patient/biometrics'}
                >
                  {profileData?.biometrics ? 'Update Biometrics' : 'Add Biometrics'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CloudUpload />}
                  fullWidth
                  onClick={() => window.location.href = '/onboarding/patient/documents'}
                >
                  {profileData?.documents?.length > 0 ? `Documents (${profileData.documents.length})` : 'Upload Documents'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<History />}
                  fullWidth
                  onClick={() => window.location.href = '/onboarding/patient/history'}
                >
                  {profileData?.medical_history?.length > 0 ? `History (${profileData.medical_history.length})` : 'Add Medical History'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Assignment />}
                  fullWidth
                  onClick={() => window.location.href = '/onboarding/patient/intake'}
                >
                  {profileData?.intake_form ? 'Update Intake Form' : 'Complete Intake Form'}
                </Button>
              </Stack>
            </Paper>

            {/* Account Settings */}
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Account Settings
              </Typography>
              <Stack spacing={2}>
                <Button
                  variant="outlined"
                  startIcon={<SecurityIcon />}
                  fullWidth
                  onClick={() => setShowPasswordDialog(true)}
                >
                  Change Password
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<NotificationsIcon />}
                  onClick={() => toast.info('Notification settings feature coming soon!')}
                  fullWidth
                >
                  Notification Settings
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<LanguageIcon />}
                  onClick={() => toast.info('Language settings feature coming soon!')}
                  fullWidth
                >
                  Language & Region
                </Button>
              </Stack>
            </Paper>
          </Stack>
        </Grid>

        {/* Profile Information */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Box>
                <Typography variant="h6">Personal Information</Typography>
                <Typography variant="body2" color="text.secondary">
                  Complete your profile to get personalized healthcare recommendations
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {isEditing && (
                  <Button
                    startIcon={<CancelIcon />}
                    variant="outlined"
                    onClick={() => setIsEditing(false)}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  startIcon={isEditing ? <SaveIcon /> : <EditIcon />}
                  variant={isEditing ? 'contained' : 'outlined'}
                  onClick={() => {
                    if (isEditing) {
                      console.log('Saving profile...');
                      handleSaveProfile();
                    } else {
                      console.log('Entering edit mode...');
                      setIsEditing(true);
                    }
                  }}
                  disabled={loading}
                >
                  {isEditing ? (loading ? 'Saving...' : 'Save Changes') : 'Edit Profile'}
                </Button>
              </Box>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={formData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Date of Birth"
                    value={formData.dateOfBirth}
                    onChange={(date) => handleInputChange('dateOfBirth', date)}
                    disabled={!isEditing}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        disabled: !isEditing
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={!isEditing}>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    value={formData.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                    label="Gender"
                  >
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                    <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={formData.phoneE164}
                  onChange={(e) => handleInputChange('phoneE164', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>
                  <Chip label="Address Information" />
                </Divider>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 1"
                  value={formData.addressLine1}
                  onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 2"
                  value={formData.addressLine2}
                  onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="State"
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  value={formData.postalCode}
                  onChange={(e) => handleInputChange('postalCode', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>
                  <Chip label="Emergency Contact" />
                </Divider>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Emergency Contact Name"
                  value={formData.emergencyContactName}
                  onChange={(e) => handleInputChange('emergencyContactName', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Emergency Contact Phone"
                  value={formData.emergencyContactPhone}
                  onChange={(e) => handleInputChange('emergencyContactPhone', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              
              {/* Medical & Personal Information */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>
                  <Chip label="Medical & Personal Information" />
                </Divider>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={!isEditing}>
                  <InputLabel>Blood Group</InputLabel>
                  <Select
                    value={formData.bloodGroup}
                    onChange={(e) => handleInputChange('bloodGroup', e.target.value)}
                    label="Blood Group"
                  >
                    <MenuItem value="">Select Blood Group</MenuItem>
                    <MenuItem value="A+">A+</MenuItem>
                    <MenuItem value="A-">A-</MenuItem>
                    <MenuItem value="B+">B+</MenuItem>
                    <MenuItem value="B-">B-</MenuItem>
                    <MenuItem value="AB+">AB+</MenuItem>
                    <MenuItem value="AB-">AB-</MenuItem>
                    <MenuItem value="O+">O+</MenuItem>
                    <MenuItem value="O-">O-</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={!isEditing}>
                  <InputLabel>Marital Status</InputLabel>
                  <Select
                    value={formData.maritalStatus}
                    onChange={(e) => handleInputChange('maritalStatus', e.target.value)}
                    label="Marital Status"
                  >
                    <MenuItem value="">Select Marital Status</MenuItem>
                    <MenuItem value="single">Single</MenuItem>
                    <MenuItem value="married">Married</MenuItem>
                    <MenuItem value="divorced">Divorced</MenuItem>
                    <MenuItem value="widowed">Widowed</MenuItem>
                    <MenuItem value="separated">Separated</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Education & Employment */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>
                  <Chip label="Education & Employment" />
                </Divider>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={!isEditing}>
                  <InputLabel>Education Level</InputLabel>
                  <Select
                    value={formData.education}
                    onChange={(e) => handleInputChange('education', e.target.value)}
                    label="Education Level"
                  >
                    <MenuItem value="">Select Education Level</MenuItem>
                    <MenuItem value="primary">Primary School</MenuItem>
                    <MenuItem value="secondary">Secondary School</MenuItem>
                    <MenuItem value="higher_secondary">Higher Secondary</MenuItem>
                    <MenuItem value="bachelor">Bachelor's Degree</MenuItem>
                    <MenuItem value="master">Master's Degree</MenuItem>
                    <MenuItem value="doctorate">Doctorate</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={!isEditing}>
                  <InputLabel>Employment Status</InputLabel>
                  <Select
                    value={formData.employmentStatus}
                    onChange={(e) => handleInputChange('employmentStatus', e.target.value)}
                    label="Employment Status"
                  >
                    <MenuItem value="">Select Employment Status</MenuItem>
                    <MenuItem value="employed">Employed</MenuItem>
                    <MenuItem value="unemployed">Unemployed</MenuItem>
                    <MenuItem value="student">Student</MenuItem>
                    <MenuItem value="retired">Retired</MenuItem>
                    <MenuItem value="self_employed">Self Employed</MenuItem>
                    <MenuItem value="homemaker">Homemaker</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Occupation"
                  value={formData.occupation}
                  onChange={(e) => handleInputChange('occupation', e.target.value)}
                  disabled={!isEditing}
                  placeholder="Your profession or job title"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Employer"
                  value={formData.employer}
                  onChange={(e) => handleInputChange('employer', e.target.value)}
                  disabled={!isEditing}
                  placeholder="Company or organization name"
                />
              </Grid>

              {/* Insurance Information */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>
                  <Chip label="Insurance Information" />
                </Divider>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Insurance Provider"
                  value={formData.insuranceProvider}
                  onChange={(e) => handleInputChange('insuranceProvider', e.target.value)}
                  disabled={!isEditing}
                  placeholder="Insurance company name"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Policy Number"
                  value={formData.insurancePolicyNumber}
                  onChange={(e) => handleInputChange('insurancePolicyNumber', e.target.value)}
                  disabled={!isEditing}
                  placeholder="Insurance policy number"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Group Number"
                  value={formData.insuranceGroupNumber}
                  onChange={(e) => handleInputChange('insuranceGroupNumber', e.target.value)}
                  disabled={!isEditing}
                  placeholder="Group number (if applicable)"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Settings and Actions */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            {/* Quick Actions */}
            {/* <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Stack spacing={2}>
                <Button
                  variant="outlined"
                  startIcon={<SecurityIcon />}
                  onClick={() => setShowPasswordDialog(true)}
                  fullWidth
                >
                  Change Password
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<NotificationsIcon />}
                  onClick={() => {
                    // Navigate to notification settings or show notification settings dialog
                    toast.info('Notification settings feature coming soon!');
                  }}
                  fullWidth
                >
                  Notification Settings
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<LanguageIcon />}
                  onClick={() => {
                    // Navigate to language settings or show language settings dialog
                    toast.info('Language settings feature coming soon!');
                  }}
                  fullWidth
                >
                  Language Settings
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<AccessibilityIcon />}
                  onClick={() => window.location.href = '/patient-dashboard/biometrics'}
                  fullWidth
                >
                  Update Biometrics
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<Assignment />}
                  onClick={() => window.location.href = '/patient-dashboard/intake-form'}
                  fullWidth
                >
                  Complete Intake Form
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<CloudUpload />}
                  onClick={() => window.location.href = '/patient-dashboard/documents'}
                  fullWidth
                >
                  Upload Documents
                </Button>
              </Stack>
            </Paper> */}

            {/* Trial Information */}
            {onboardingState?.trial && (
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Trial Status
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    You're currently on a free trial. Complete your profile to unlock full features.
                  </Typography>
                </Alert>
                <Typography variant="body2" color="text.secondary">
                  Trial ends: {new Date(onboardingState.trial.ends_at).toLocaleDateString()}
                </Typography>
              </Paper>
            )}

            {/* Health Metrics Summary */}
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Health Metrics Summary
              </Typography>
              {profileData?.biometrics ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                      <Typography variant="h4" color="primary">
                        {profileData.biometrics.bmi ? profileData.biometrics.bmi.toFixed(1) : '--'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        BMI
                      </Typography>
                      {profileData.biometrics.bmi_category && (
                        <Chip 
                          label={profileData.biometrics.bmi_category.charAt(0).toUpperCase() + profileData.biometrics.bmi_category.slice(1)} 
                          size="small" 
                          color={profileData.biometrics.bmi_category === 'normal' ? 'success' : 
                                 profileData.biometrics.bmi_category === 'underweight' ? 'info' : 'warning'}
                          sx={{ mt: 1 }}
                        />
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                      <Typography variant="h4" color="primary">
                        {profileData.biometrics.blood_pressure_systolic ? 
                          `${profileData.biometrics.blood_pressure_systolic}/${profileData.biometrics.blood_pressure_diastolic}` : '--'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Blood Pressure (mmHg)
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                      <Typography variant="h4" color="primary">
                        {profileData.biometrics.heart_rate || '--'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Heart Rate (bpm)
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                      <Typography variant="h4" color="primary">
                        {profileData.biometrics.weight_kg ? `${profileData.biometrics.weight_kg} kg` : '--'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Weight
                      </Typography>
                    </Box>
                  </Grid>
                  
                  {/* Additional metrics */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                      <Typography variant="h4" color="primary">
                        {profileData.biometrics.height_cm ? `${profileData.biometrics.height_cm} cm` : '--'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Height
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                      <Typography variant="h4" color="primary">
                        {profileData.biometrics.temperature ? `${profileData.biometrics.temperature}°C` : '--'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Temperature
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                      <Typography variant="h4" color="primary">
                        {profileData.biometrics.respiratory_rate || '--'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Respiratory Rate
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                      <Typography variant="h4" color="primary">
                        {profileData.biometrics.waist_to_hip_ratio ? profileData.biometrics.waist_to_hip_ratio.toFixed(2) : '--'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Waist-to-Hip Ratio
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Complete your biometrics to see health metrics summary.
                  </Typography>
                </Alert>
              )}
            </Paper>

            {/* Profile Status */}
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Profile Status
              </Typography>
              {onboardingState && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Profile Completion: {calculateCompletionPercentage()}%
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={calculateCompletionPercentage()} 
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              )}
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    {profileExists ? <CheckCircle color="success" /> : <RadioButtonUnchecked />}
                  </ListItemIcon>
                  <ListItemText 
                    primary="Basic Profile" 
                    secondary={profileExists ? "Completed" : "Not completed"}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {getSectionStatus('biometrics') === 'completed' ? 
                      <CheckCircle color="success" /> : <RadioButtonUnchecked />}
                  </ListItemIcon>
                  <ListItemText 
                    primary="Biometrics & Health" 
                    secondary={getSectionStatus('biometrics') === 'completed' ? "Completed" : "Not completed"}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {getSectionStatus('emergency') === 'completed' ? 
                      <CheckCircle color="success" /> : <RadioButtonUnchecked />}
                  </ListItemIcon>
                  <ListItemText 
                    primary="Emergency Contact" 
                    secondary={getSectionStatus('emergency') === 'completed' ? "Completed" : "Not completed"}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {getSectionStatus('history') === 'completed' ? 
                      <CheckCircle color="success" /> : <RadioButtonUnchecked />}
                  </ListItemIcon>
                  <ListItemText 
                    primary="Medical History" 
                    secondary={getSectionStatus('history') === 'completed' ? "Completed" : "Not completed"}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {getSectionStatus('uploads') === 'completed' ? 
                      <CheckCircle color="success" /> : <RadioButtonUnchecked />}
                  </ListItemIcon>
                  <ListItemText 
                    primary="Document Uploads" 
                    secondary={getSectionStatus('uploads') === 'completed' ? "Completed" : "Not completed"}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {getSectionStatus('intake') === 'completed' ? 
                      <CheckCircle color="success" /> : <RadioButtonUnchecked />}
                  </ListItemIcon>
                  <ListItemText 
                    primary="Intake Form" 
                    secondary={getSectionStatus('intake') === 'completed' ? "Completed" : "Not completed"}
                  />
                </ListItem>
              </List>
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      {/* Password Change Dialog */}
      <Dialog open={showPasswordDialog} onClose={() => setShowPasswordDialog(false)}>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Current Password"
              type="password"
              name="currentPassword"
              value={passwordData.currentPassword}
              onChange={handlePasswordChange}
            />
            <TextField
              fullWidth
              label="New Password"
              type="password"
              name="newPassword"
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              name="confirmPassword"
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPasswordDialog(false)}>Cancel</Button>
          <Button onClick={handleSavePassword} variant="contained">
            Save Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile;
